import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, Trash2, Globe, ExternalLink, Settings } from 'lucide-react';
import { statusPagesAPI, monitorsAPI } from '../api/client';
import { Monitor, StatusPage } from '../types';
import { HelpLink } from '../components/layout/HelpLink';

/**
 * What customers point their own domain at.
 *
 * A hostname rather than this server's address on purpose: it is handed out to
 * customers and ends up in DNS zones we do not control, so moving the platform
 * to another machine has to be one record change here rather than a migration
 * every customer has to perform.
 */
const CUSTOM_DOMAIN_TARGET = 'connect.uptimer.tsew.com';

export function StatusPages() {
  const queryClient = useQueryClient();
  const { data: pages = [], isLoading } = useQuery({ queryKey: ['status-pages'], queryFn: statusPagesAPI.list });
  const [showCreate, setShowCreate] = useState(false);
  const [editingID, setEditingID] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', slug: '', primary_color: '#10B981', language: 'en' });

  const createPage = useMutation({
    mutationFn: (data: Partial<StatusPage>) => statusPagesAPI.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['status-pages'] }); setShowCreate(false); },
  });

  const deletePage = useMutation({
    mutationFn: (id: string) => statusPagesAPI.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['status-pages'] }),
  });

  if (isLoading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>Status Pages</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <HelpLink topic="status-pages" label="Status Pages" />
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Create Status Page</button>
        </div>
      </div>

      {pages.length === 0 ? (
        <div className="empty-state">
          <Globe size={48} />
          <h3>No status pages</h3>
          <p>Create a public status page to keep your users informed about service health.</p>
        </div>
      ) : (
        <div className="grid-2">
          {pages.map((page: StatusPage, i: number) => (
            <motion.div key={page.id} className="card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <div className="card-header">
                <div>
                  <div className="card-title">{page.name}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>/{page.slug}</div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <a href={`/status/${page.slug}`} target="_blank" rel="noopener" className="btn btn-ghost btn-sm" aria-label={`Open ${page.name}`}><ExternalLink size={14} /></a>
                  <button className="btn btn-ghost btn-sm" aria-label={`Edit ${page.name}`} onClick={() => setEditingID(page.id)}><Settings size={14} /></button>
                  <button className="btn btn-ghost btn-sm" aria-label={`Delete ${page.name}`} onClick={() => { if (confirm('Delete this status page?')) deletePage.mutate(page.id); }}><Trash2 size={14} /></button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                <span>Color: <span style={{ color: page.primary_color }}>●</span> {page.primary_color}</span>
                <span>Language: {page.language}</span>
                {page.is_password_protected && <span className="badge badge-paused">Password Protected</span>}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <motion.div className="modal" onClick={e => e.stopPropagation()} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="modal-header">
              <h2 className="modal-title">Create Status Page</h2>
              <button className="modal-close" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <form onSubmit={e => { e.preventDefault(); createPage.mutate(form); }}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="My Service Status" />
              </div>
              <div className="form-group">
                <label className="form-label">Slug (URL path)</label>
                <input className="form-input" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} required placeholder="my-service" />
              </div>
              <div className="form-group">
                <label className="form-label">Primary Color</label>
                <input type="color" className="form-input" value={form.primary_color} onChange={e => setForm({ ...form, primary_color: e.target.value })} style={{ height: 40, padding: 4 }} />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={createPage.isPending}>Create</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {editingID && <EditStatusPage id={editingID} onClose={() => setEditingID(null)} />}
    </div>
  );
}

// The editor loads the page on its own rather than reusing the list row: only
// the detail endpoint returns which monitors the page publishes, and that
// selection is the thing people actually come here to change.
function EditStatusPage({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: page, isLoading } = useQuery({ queryKey: ['status-page', id], queryFn: () => statusPagesAPI.get(id) });
  const { data: monitors = [] } = useQuery({ queryKey: ['monitors'], queryFn: monitorsAPI.list });

  const [form, setForm] = useState<Partial<StatusPage> | null>(null);
  const [selected, setSelected] = useState<string[] | null>(null);
  const [error, setError] = useState('');

  // Seed the form once the page arrives; afterwards the local copy wins so
  // background refetches can't overwrite what is being typed.
  if (page && form === null) {
    setForm({
      name: page.name,
      slug: page.slug,
      primary_color: page.primary_color,
      language: page.language,
      logo_url: page.logo_url ?? '',
      custom_domain: page.custom_domain ?? '',
      announcement: page.announcement ?? '',
      hide_from_search: page.hide_from_search,
      is_password_protected: page.is_password_protected,
      password: '',
    });
    setSelected((page.monitors ?? []).map(m => m.monitor_id));
  }

  const save = useMutation({
    mutationFn: async () => {
      await statusPagesAPI.update(id, form ?? {});
      await statusPagesAPI.setMonitors(id, selected ?? []);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['status-pages'] });
      queryClient.invalidateQueries({ queryKey: ['status-page', id] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  function toggleMonitor(monitorID: string) {
    setSelected(prev => (prev ?? []).includes(monitorID)
      ? (prev ?? []).filter(m => m !== monitorID)
      : [...(prev ?? []), monitorID]);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div className="modal" onClick={e => e.stopPropagation()} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
        <div className="modal-header">
          <h2 className="modal-title">Edit Status Page</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {isLoading || !form ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : (
          <form onSubmit={e => { e.preventDefault(); setError(''); save.mutate(); }}>
            <div className="form-group">
              <label className="form-label" htmlFor="sp-name">Name</label>
              <input id="sp-name" className="form-input" value={form.name ?? ''} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sp-slug">Slug (URL path)</label>
              <input id="sp-slug" className="form-input" value={form.slug ?? ''} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sp-announcement">Announcement</label>
              <textarea id="sp-announcement" className="form-input" rows={3} value={form.announcement ?? ''} onChange={e => setForm({ ...form, announcement: e.target.value })} placeholder="Shown at the top of the public page" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sp-logo">Logo URL</label>
              <input id="sp-logo" className="form-input" value={form.logo_url ?? ''} onChange={e => setForm({ ...form, logo_url: e.target.value })} placeholder="https://example.com/logo.svg" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sp-domain">Custom Domain</label>
              <input id="sp-domain" className="form-input" value={form.custom_domain ?? ''} onChange={e => setForm({ ...form, custom_domain: e.target.value })} placeholder="status.example.com" />
              <div className="form-hint">
                Point a CNAME at <code>{CUSTOM_DOMAIN_TARGET}</code>. The certificate is issued
                automatically the first time the domain is visited — nothing else to set up.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sp-color">Primary Color</label>
              <input id="sp-color" type="color" className="form-input" value={form.primary_color ?? '#10B981'} onChange={e => setForm({ ...form, primary_color: e.target.value })} style={{ height: 40, padding: 4 }} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="sp-language">Language</label>
              <input id="sp-language" className="form-input" value={form.language ?? ''} onChange={e => setForm({ ...form, language: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input type="checkbox" checked={form.hide_from_search ?? false} onChange={e => setForm({ ...form, hide_from_search: e.target.checked })} />
                Hide from search engines
              </label>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={form.is_password_protected ?? false}
                  onChange={e => setForm({ ...form, is_password_protected: e.target.checked, password: e.target.checked ? form.password : '' })}
                />
                Require a password to view
              </label>
            </div>

            {form.is_password_protected && (
              <div className="form-group">
                <label className="form-label" htmlFor="sp-password">
                  {page?.is_password_protected ? 'New password' : 'Password'}
                </label>
                <input
                  id="sp-password"
                  type="password"
                  className="form-input"
                  value={form.password ?? ''}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  // A page that is already protected keeps its password when
                  // this is left empty: the server never sends the hash out, so
                  // there is nothing to prefill it with.
                  placeholder={page?.is_password_protected ? 'Leave blank to keep the current password' : 'Choose a password'}
                  required={!page?.is_password_protected}
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Monitors on this page</label>
              {monitors.length === 0 ? (
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                  No monitors yet — create one first and it will show up here.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: 220, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)' }}>
                  {monitors.map((m: Monitor) => (
                    <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                      <input type="checkbox" checked={(selected ?? []).includes(m.id)} onChange={() => toggleMonitor(m.id)} />
                      <span>{m.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {error && <div style={{ color: 'var(--color-down)', fontSize: 'var(--font-size-sm)' }}>{error}</div>}

            <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={save.isPending}>Save Changes</button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
